import React, { useCallback, useState, useMemo, memo } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useMediaQuery } from 'react-responsive';
import { format } from 'date-fns/format';
import classNames from 'classnames';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import {
  CalendarBooked as CalendarBookedIcon,
  Calendar as CalendarIcon,
} from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import { LinkAsButton } from '@johnlewispartnership/wtr-ingredients/ingredients/LinkAsButton';
import Spinner from '@johnlewispartnership/wtr-ingredients/dist/Spinner';
import Alert from '@johnlewispartnership/wtr-ingredients/dist/ingredients/Alert';
import AnchorLink from 'components/AnchorLink';
import { BREAKPOINTS } from 'constants/grid';
import { urls } from 'constants/urls';
import { formatSlotTime } from 'utils/format-slot-time';
import { KEY_ENTER } from 'constants/keys';
import { isCollectionSlotType } from 'utils/slots';
import { useTrolley } from 'contexts/Trolley';
import { SlotType } from 'services/slotOrchestration/types';
import { useSlotContext } from 'contexts/Slot';
import styles from './SlotButton.module.scss';
import { formatSlotButtonText } from './utils/format-slot-button';

export const SlotButton = memo(() => {
  const {
    slotContext: {
      loading: slotLoading,
      booked: slotBooked,
      details: slotDetails,
      address: slotAddress,
    },
  } = useSlotContext();

  const { line1, line2, name, postalCode, town, region } = slotAddress || {};

  const isMobile = useMediaQuery({ maxWidth: BREAKPOINTS.md });

  const testIdSuffix = isMobile ? 'mobile' : 'desktop';

  const { trackEvent } = useAnalytics();
  const { state: trolleyState } = useTrolley();

  const [hidden, setHidden] = useState(true);

  const handleChangeSlot = useCallback(() => {
    setHidden(true);

    trackEvent({
      event: 'change_slot_button_click',
      eventAction: 'ClickChangeSlot',
      eventCategory: 'On-Page Interactions',
      eventLabel: `ViewSlotBooked`,
      eventValue: undefined,
    });
  }, [trackEvent]);

  const handleClickOutside = useCallback(() => {
    if (!hidden) {
      setHidden(true);
    }
  }, [hidden]);

  const toggleVisibility = useCallback(() => {
    if (hidden) {
      trackEvent({
        event: 'slot_details_button_click',
        eventAction: 'ClickSlotDetails',
        eventCategory: 'On-Page Interactions',
        eventLabel: `Header`,
        eventValue: undefined,
      });
    }

    setHidden(!hidden);
  }, [hidden, trackEvent]);

  const handleKeyDown = useCallback(
    event => {
      const { keyCode } = event;

      if (keyCode === KEY_ENTER) {
        handleChangeSlot();
      }
    },
    [handleChangeSlot],
  );

  const handleLinkClick = () => {
    trackEvent({
      event: 'click_book_slot',
    });
  };

  const isCollection = isCollectionSlotType(slotDetails?.slotType);

  const spinnerTheme = isMobile ? 'dark' : 'light';

  const slotButtonText = slotLoading
    ? ''
    : formatSlotButtonText({
        isSlotBooked: slotBooked,
        slotDate: slotDetails?.startDateTime,
        shortTime: isMobile,
      });

  const hasSlotInformation = slotBooked && slotDetails?.startDateTime;

  const longDate = useMemo(
    () =>
      hasSlotInformation
        ? `${format(slotDetails?.startDateTime, 'eeee')} ${format(
            slotDetails?.startDateTime,
            'dd',
          )} ${format(slotDetails?.startDateTime, 'MMMM')}`
        : '',
    [hasSlotInformation, slotDetails?.startDateTime],
  );

  const longTime = useMemo(
    () =>
      hasSlotInformation
        ? `${formatSlotTime(slotDetails?.startDateTime)}-${formatSlotTime(
            slotDetails?.endDateTime,
          )}`
        : '',
    [hasSlotInformation, slotDetails?.endDateTime, slotDetails?.startDateTime],
  );

  const slotButtonLabelId = `slot-button-label-${testIdSuffix}`;
  const blurButton = () => setHidden(true);
  const focusButton = () => setHidden(false);
  const changeSlotUrl = useMemo(() => {
    switch (slotDetails?.slotType) {
      case SlotType.DELIVERY:
        return urls.bookDeliverySlot;
      case SlotType.ENTERTAINING_COLLECTION:
        return urls.bookEntertainingCollectionSlot;
      case SlotType.GROCERY_COLLECTION:
        return urls.bookGroceriesAndEntertainingCollectionSlot;
      default:
        return urls.serviceSelection;
    }
  }, [slotDetails?.slotType]);

  let slotButtonLabel = slotBooked ? 'Slot' : 'Book a slot';
  slotButtonLabel = slotLoading
    ? 'Loading slots. Click here to go to slots page.'
    : slotButtonLabel;

  return (
    <OutsideClickHandler onOutsideClick={handleClickOutside}>
      <div className={styles.wrapper} data-testid={`slot-button-${testIdSuffix}`}>
        <div>
          {slotBooked ? (
            <AnchorLink
              aria-label={slotButtonLabel}
              {...{
                component: 'button',
                type: 'button',
                'data-testid': `slot-details-button-${testIdSuffix}`,
                onClick: toggleVisibility,
                underline: 'hover',
                className: styles.slotButton,
              }}
            >
              <CalendarBookedIcon className={styles.accountMenuIcon} size="small" />
              <span className={styles.slotButtonText}>{slotButtonText}</span>
            </AnchorLink>
          ) : (
            <AnchorLink
              aria-label={slotButtonLabel}
              {...{
                'data-testid': `book-slot-button-${testIdSuffix}`,
                id: 'slot-button',
                onClick: handleLinkClick,
                to: urls.serviceSelection,
                underline: 'hover',
                className: styles.slotButton,
              }}
            >
              {slotLoading ? (
                <Spinner isActive={slotLoading} size="small" theme={spinnerTheme} />
              ) : (
                <>
                  <CalendarIcon className={styles.accountMenuIcon} size="small" />
                  <span className={styles.slotButtonText}>{slotButtonText}</span>
                </>
              )}
            </AnchorLink>
          )}
        </div>
        {slotBooked && (
          <div className={styles.detailsWrapper}>
            <div
              className={classNames(styles.details, { 'sr-only': hidden })}
              data-testid={`slot-details-${testIdSuffix}`}
            >
              <div className={styles.header}>
                <header aria-hidden="true">
                  Your {isCollection ? 'collection' : 'delivery'} slot
                </header>
              </div>
              <div className={styles.slotDateTime}>
                <span className="sr-only">Booked for:</span>
                <span>{longDate}</span>
                <span className={styles.slotTime}>{longTime}</span>
              </div>
              <div
                className={styles.deliveryDetails}
                data-cs-mask
                data-testid={`slot-address-details-${testIdSuffix}`}
              >
                <span className={styles.title}>
                  {isCollection ? 'Collect from:' : 'Delivery to:'}
                </span>
                {isCollection && name && <span className={styles.address}>{name}</span>}
                <span className={styles.address}>{line1}</span>
                {line2 && <span className={styles.address}>{line2}</span>}
                <span className={styles.address}>{town}</span>
                {region && <span className={styles.address}>{region}</span>}
                <span className={styles.address}>{postalCode}</span>
              </div>
              {trolleyState.trolley?.amendingOrder && !trolleyState.slotChangeable ? (
                <div className={styles.alert}>
                  <Alert
                    data-testid="slot-details-alert"
                    message="Your order contains items currently being prepared. We are unable to change the delivery/collection time or cancel the order."
                    small
                    type="warning"
                  />
                </div>
              ) : (
                <div className={styles.button}>
                  <span className="sr-only" htmlFor="changeSlotButton" id={slotButtonLabelId}>
                    {isCollection ? 'Collection' : 'Delivery'} slot booked for {longDate} {longTime}
                    . {isCollection ? 'Collect from' : 'Delivery to'} {line1},
                    {line2 ? ` ${line2},` : ''} {town},{region ? ` ${region},` : ''} {postalCode}.
                    Change slot.
                  </span>
                  <LinkAsButton
                    aria-describedby={slotButtonLabelId}
                    aria-label="Change slot"
                    data-testid={`change-slot-button-${testIdSuffix}`}
                    label="Change slot"
                    onBlur={blurButton}
                    onClick={handleChangeSlot}
                    onFocus={focusButton}
                    onKeyDown={handleKeyDown}
                    theme="secondary"
                    to={changeSlotUrl}
                    type="button"
                    width="full"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </OutsideClickHandler>
  );
});
